// src/Components/Drawer/DocsTab/DocsTab.js

import React, { useRef, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { ref, deleteObject } from 'firebase/storage';
import { storage } from '../../../Firebase/firebase';
import { DrawerService } from '../CustomDrawer/DrawerService';
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';

const DARK_BLUE = '#00008B';
const ORANGE_COLOR = 'orange';

const styles = {
  label: {
    color: DARK_BLUE,
    fontWeight: 'bold',
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    marginBottom: '0px',
    lineHeight: 1.2,
  },
  valueBox: {
    borderRadius: '4px',
    minHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    overflow: 'hidden',
    backgroundColor: '#fff',
    border: `1px solid ${ORANGE_COLOR}`,
    width: '100%',
  },
  checkboxBox: {
    borderRadius: '4px',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    border: `1px solid ${ORANGE_COLOR}`,
    cursor: 'pointer',
  },
  text: {
    border: 'none',
    outline: 'none',
    width: '100%',
    backgroundColor: 'transparent',
    fontSize: '0.8rem',
    fontFamily: 'inherit',
    color: DARK_BLUE,
    padding: 0,
    margin: 0,
    cursor: 'default',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  checkbox: {
    padding: 0,
    '&.Mui-checked': {
      color: DARK_BLUE,
    },
  },
};

const DocsTab = ({
  selectedMarker,
  triggerAdd,
  triggerDelete,
  items,
  setItems,
  selectedIds,
  setSelectedIds,
  isSaving,
}) => {
  const fileInputRef = useRef(null);
  const prevDeleteTriggerRef = useRef(triggerDelete);
  const prevAddTriggerRef = useRef(triggerAdd);

  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [alert, setAlert] = useState({ visible: false, severity: 'info', message: '' });

  const showTempAlert = (severity, message) => {
    setAlert({ visible: true, severity, message });
    setTimeout(() => setAlert({ visible: false, severity: 'info', message: '' }), 2000);
  };

  const handleFileChange = async (event) => {
    const rawFiles = event.target.files;
    if (!rawFiles || rawFiles.length === 0) return;

    const filesArray = Array.from(rawFiles);

    try {
      flushSync(() => {
        setIsProcessing(true);
      });
    } catch (e) {
      setIsProcessing(true);
    }

    setTimeout(async () => {
      try {
        const tempItems = filesArray.map((file, index) => ({
          id: `${Date.now()}-${Math.random()}-${index}`,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          fileObject: file,
          timestamp: new Date().toISOString(),
          url: null,
        }));

        const combinedItems = [...items, ...tempItems];
        // AMENDED: Destructure the returned object to extract the array state
        const { updatedItemsState } = await DrawerService.saveDocs(
          selectedMarker,
          combinedItems,
        );

        setItems(updatedItemsState, false);
        showTempAlert('success', 'Documents uploaded successfully.');
      } catch (error) {
        console.error('[DocsTab] Upload Error:', error);
        showTempAlert('error', 'Failed to upload documents.');
      } finally {
        setIsProcessing(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }, 300);
  };

  useEffect(() => {
    if (triggerAdd && triggerAdd > 0 && triggerAdd !== prevAddTriggerRef.current) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      fileInputRef.current && fileInputRef.current.click();
      prevAddTriggerRef.current = triggerAdd;
    }
  }, [triggerAdd]);

  useEffect(() => {
    if (
      triggerDelete &&
      triggerDelete > 0 &&
      triggerDelete !== prevDeleteTriggerRef.current
    ) {
      if (selectedIds.size === 0) showTempAlert('info', 'Select documents to delete.');
      else setShowDeleteConfirm(true);
      prevDeleteTriggerRef.current = triggerDelete;
    }
  }, [triggerDelete, selectedIds]);

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    setIsProcessing(true);

    try {
      const removedItems = items.filter((item) => selectedIds.has(item.id));
      const groupId = selectedMarker.markerGroupID || selectedMarker.id;

      for (const delItem of removedItems) {
        if (!delItem.fileObject && delItem.url && delItem.url.startsWith('http')) {
          try {
            const cleanName = delItem.fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
            const storagePath = `documents/${groupId}/${delItem.id}_${cleanName}`;
            const deleteRef = ref(storage, storagePath);
            await deleteObject(deleteRef);
          } catch (err) {
            console.warn('[DocsTab] Storage delete warning:', err);
          }
        }
      }

      const remainingItems = items.filter((item) => !selectedIds.has(item.id));
      // AMENDED: Destructure the returned object to extract the array state
      const { updatedItemsState } = await DrawerService.saveDocs(
        selectedMarker,
        remainingItems,
      );

      setItems(updatedItemsState, false);
      setSelectedIds(new Set());
      showTempAlert('success', 'Documents deleted successfully.');
    } catch (error) {
      console.error('[DocsTab] Delete Failed:', error);
      showTempAlert('error', 'Failed to delete documents.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(new Set(items.map((item) => item.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleRowClick = (event, id) => {
    event.stopPropagation();
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const isAllSelected = items.length > 0 && selectedIds.size === items.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < items.length;

  if (!selectedMarker) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
          Click on a marker to add information, photos and documents.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        backgroundColor: '#fff',
        position: 'relative',
      }}>
      {(isProcessing || isSaving) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            zIndex: 100002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'wait',
          }}>
          <CircularProgress sx={{ color: '#00B0FF' }} />
        </Box>
      )}

      <ConfirmDialog
        open={alert.visible}
        message={alert.message}
        severity={alert.severity}
        sx={{ zIndex: 100005 }}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        message='Delete selected documents permanently?'
        severity='warning'
        sx={{ zIndex: 100005 }}
      />

      <input
        type='file'
        multiple
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ backgroundColor: 'transparent', flex: 1, overflow: 'auto' }}>
        <Table
          size='small'
          stickyHeader
          sx={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}>
          <TableHead>
            <TableRow>
              <TableCell
                padding='checkbox'
                sx={{
                  width: '40px',
                  borderBottom: 'none',
                  pb: 0,
                  backgroundColor: '#fff',
                }}>
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={items.length === 0}
                  size='small'
                  sx={styles.checkbox}
                />
              </TableCell>
              <TableCell
                sx={{ borderBottom: 'none', pb: 0, pl: 1, backgroundColor: '#fff' }}>
                <Typography sx={styles.label}>DOCUMENT NAME</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(items) && items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align='center'
                  sx={{ borderBottom: 'none', pt: 2 }}>
                  <Typography variant='caption' color='text.secondary' fontStyle='italic'>
                    No documents added
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              Array.isArray(items) &&
              items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  onClick={(e) => handleRowClick(e, item.id)}
                  onDoubleClick={() => item.url && window.open(item.url, '_blank')}
                  role='checkbox'
                  selected={selectedIds.has(item.id)}
                  sx={{ cursor: 'pointer', '& td': { borderBottom: 'none' } }}>
                  <TableCell padding='none'>
                    <Box sx={{ display: 'flex', justifyContent: 'center', pl: '4px' }}>
                      <Box sx={styles.checkboxBox}>
                        <Checkbox
                          checked={selectedIds.has(item.id)}
                          size='small'
                          sx={styles.checkbox}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ pl: 1 }}>
                    <Box sx={styles.valueBox} title={item.fileName}>
                      <Typography sx={styles.text}>{item.fileName}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DocsTab;
